import { makeAutoObservable } from 'mobx';
import { ProductLineId } from '@insurancemenu/imenu-components-lib/dist/types/ProductLineId';
import { CarriersPerProductLine, ProductLineCardStatus } from './pageStore';
import CarrierDelegate from './carrierDelegate';

export interface CarriersPerProductLineDelegates {
  productLineId: ProductLineId;
  carriers: CarrierDelegate[];
  status: ProductLineCardStatus;
}

export default class ProductLineDelegate {
  /** Transformed product line info with additional fields for ui purposes */
  private _carriersPerProductLine: CarriersPerProductLineDelegates;

  public get productLine(): CarriersPerProductLineDelegates {
    return this._carriersPerProductLine;
  }

  /* Update the product line status based on remaining selected carriers:
   * - If at least one selected carrier has missing recipients, status should be MISSING_RECIPIENTS
   * - If all selected carriers have valid recipients, status should be INCLUDED_IN_REQUEST
   * - If no carriers are selected, status should be NO_CARRIERS_REQUESTED
   */
  private updateProductLineStatus(): void {
    const hasMissingRecipientsStatus = this._carriersPerProductLine.carriers.some(
      (carrierDelegate) => carrierDelegate.carrier.status === ProductLineCardStatus.MISSING_RECIPIENTS,
    );

    const hasIncludedInRequestStatus = this._carriersPerProductLine.carriers.some(
      (carrierDelegate) => carrierDelegate.carrier.status === ProductLineCardStatus.INCLUDED_IN_REQUEST,
    );

    // Update general product line status
    if (hasMissingRecipientsStatus) {
      this._carriersPerProductLine.status = ProductLineCardStatus.MISSING_RECIPIENTS;
    } else if (hasIncludedInRequestStatus) {
      this._carriersPerProductLine.status = ProductLineCardStatus.INCLUDED_IN_REQUEST;
    } else {
      this._carriersPerProductLine.status = ProductLineCardStatus.NO_CARRIERS_REQUESTED;
    }
  }

  private createCarriersDelegates(carrierAvailability: CarriersPerProductLine): CarriersPerProductLineDelegates {
    const carrierDelegates = carrierAvailability.carriers.map(
      (carrier) =>
        new CarrierDelegate({
          carrierData: carrier,
          updateProductLineStatus: this.updateProductLineStatus.bind(this),
        }),
    );

    return {
      ...carrierAvailability,
      carriers: carrierDelegates,
      status: ProductLineCardStatus.NO_CARRIERS_REQUESTED,
    };
  }

  constructor(productLineData: CarriersPerProductLine) {
    this._carriersPerProductLine = this.createCarriersDelegates(productLineData);

    makeAutoObservable(this, {}, { autoBind: true });
  }
}
